import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

const FR_RELATIVE = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });

export function fromNowFR(date: Date | string | null | undefined): string {
  if (!date) return "—";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "—";
  // Reject sentinel / clearly invalid dates : Go's time.Time zero value
  // serialises to "0001-01-01T00:09:21+00:09" (or similar) ; anything before
  // year 2000 in this app is a parse failure, not a real date.
  if (d.getFullYear() < 2000) return "date inconnue";
  if (d.getFullYear() > new Date().getFullYear() + 1) return "date inconnue";
  const diffMs = d.getTime() - Date.now();
  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ["year", 1000 * 60 * 60 * 24 * 365],
    ["month", 1000 * 60 * 60 * 24 * 30],
    ["week", 1000 * 60 * 60 * 24 * 7],
    ["day", 1000 * 60 * 60 * 24],
    ["hour", 1000 * 60 * 60],
    ["minute", 1000 * 60],
    ["second", 1000],
  ];
  for (const [u, ms] of units) {
    if (Math.abs(diffMs) >= ms || u === "second") {
      return FR_RELATIVE.format(Math.round(diffMs / ms), u);
    }
  }
  return "—";
}

export function formatNumber(n: number | null | undefined): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("fr-FR").format(n);
}

/**
 * Format a score with at most 2 decimals, French locale. `null/undefined`
 * become "—". Whole numbers are shown without trailing zeros (`12` not `12.00`).
 */
export function formatScore(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
}

/**
 * Build a Boursorama profile URL for a scraped member.
 * Pattern: https://www.boursorama.com/espace-membres/profil/{slug|username}/
 */
export function boursoramaUserUrl(slugOrName: string): string {
  return `https://www.boursorama.com/espace-membres/profil/${slugOrName}/`;
}

/**
 * Build a Boursorama forum-thread URL from the forum slug + thread external id.
 * Pattern: https://www.boursorama.com/bourse/forum/{forumSlug}/detail/{externalId}/
 * Prefer a stored full `url` when available — this is the fallback constructor.
 */
export function boursoramaThreadUrl(forumSlug: string, externalId: string): string {
  return `https://www.boursorama.com/bourse/forum/${forumSlug}/detail/${externalId}/`;
}
